var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import powerByGoogleSrc from '@images/powered_by_google_on_white.png';
import FormInputRegular from '@marketing/components/funnel/FormFields/FormInputRegular';
import { Asterisk } from '@marketing/components/funnel/Icons/Icons';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import scriptLoader from 'react-async-script';
import PlacesAutocomplete, { geocodeByPlaceId } from 'react-places-autocomplete';
import { addressFieldContainsPOBox } from '@utils/form_validations';
// Google maps script is needed for places API.
var _PLACES_API_URL = 'https://maps.googleapis.com/maps/api/js?' +
    'key=AIzaSyCGAM5YGMIRsw16eLw9gqs3Y-9Q7DLf-AM&libraries=places&callback=initGoogleMap';
var searchOptions = {
    types: ['address'],
    componentRestrictions: {
        country: 'us',
    },
};
/** Handles results representing an address from the Maps Geocoder API.
 * See https://goo.gl/ZRMHmR
 */
var parseGoogleMapsAddress = function (addressComponents) {
    var componentForm = {
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        postal_code: 'short_name',
        sublocality_level_1: 'short_name',
    };
    var profileAddressFields = {
        city: '',
        street: '',
        state: '',
        zipcode: '',
        apt: '',
    };
    var streetAddress = [null, null]; // ['Number', 'Street Name']
    for (var i = 0; i < addressComponents.length; i++) {
        var componentType = addressComponents[i].types[0];
        if (addressComponents[i].types.indexOf('sublocality_level_1') > -1) {
            componentType = 'sublocality_level_1';
        }
        if (!componentForm[componentType]) {
            continue;
        }
        var componentFormType = componentForm[componentType];
        switch (componentType) {
            case 'locality':
                profileAddressFields.city = addressComponents[i][componentFormType];
                break;
            case 'sublocality_level_1':
                if (profileAddressFields.city === '') {
                    profileAddressFields.city = addressComponents[i][componentFormType];
                }
                break;
            case 'street_number':
                streetAddress[0] = addressComponents[i][componentFormType];
                streetAddress[0] && streetAddress[1]
                    ? (profileAddressFields.street = streetAddress.join(' '))
                    : null;
                break;
            case 'route':
                streetAddress[1] = addressComponents[i][componentFormType];
                streetAddress[0] && streetAddress[1]
                    ? (profileAddressFields.street = streetAddress.join(' '))
                    : null;
                break;
            case 'administrative_area_level_1':
                profileAddressFields.state = addressComponents[i][componentFormType];
                break;
            case 'postal_code':
                profileAddressFields.zipcode = addressComponents[i][componentFormType];
                break;
        }
    }
    return profileAddressFields;
};
var LocationSearchInput = function (_a) {
    var id = _a.id, label = _a.label, disabled = _a.disabled, value = _a.value, onInputChange = _a.onInputChange, handleAddressSelected = _a.handleAddressSelected, google = _a.google, setIsValid = _a.setIsValid;
    var _b = useState(''), error = _b[0], setError = _b[1];
    useEffect(function () {
        if (addressFieldContainsPOBox(value)) {
            var msg = 'We currently do not deliver to P.O. boxes, please enter a new address';
            setError(msg);
            setIsValid && setIsValid(false);
        }
        else {
            setError(null);
            setIsValid && setIsValid(true);
        }
    }, [value]);
    var handleAutocompleteChange = function (value) {
        onInputChange(value);
    };
    var handleAutocompleteError = function (_, clearSuggestions) {
        clearSuggestions();
    };
    var handleAutocompleteSelect = function (_, placeId) {
        geocodeByPlaceId(placeId)
            .then(function (geocoderResults) {
            var addressObj = parseGoogleMapsAddress(geocoderResults[0].address_components);
            handleAddressSelected(addressObj);
        })
            .catch(function () {
            return;
        });
    };
    var handleRawAddressValidation = function (value) {
        var isValid = !addressFieldContainsPOBox(value);
        return {
            isValid: isValid,
            error: isValid ? '' : 'We currently do not deliver to P.O. boxes, please enter a new address',
        };
    };
    if (!google) {
        return (_jsx(_Fragment, { children: _jsx(FormInputRegular, { validate: handleRawAddressValidation, id: "street-address", label: "Street Address", value: value }) }));
    }
    return (_jsx(PlacesAutocomplete, __assign({ value: value, onChange: handleAutocompleteChange, onSelect: handleAutocompleteSelect, onError: handleAutocompleteError, debounce: 300, searchOptions: searchOptions }, { children: function (_a) {
            var getInputProps = _a.getInputProps, suggestions = _a.suggestions, getSuggestionItemProps = _a.getSuggestionItemProps, loading = _a.loading;
            var inputProps = getInputProps({
                className: clsx('w-full flex-1 text-md-med-m bg-transparent md:text-md-med-d border-0 text-center focus:outline-none pb-[2px]', loading && 'loading'),
                autoComplete: 'shipping street-address',
                name: id,
                disabled: disabled,
            });
            return (_jsxs("div", __assign({ className: "relative w-full flex flex-col justify-start gap-1 items-start" }, { children: [_jsx("div", __assign({ className: "flex justify-start items-end border-b-2 border-border-shoe w-full" }, { children: _jsx("input", __assign({ id: id, minLength: 3, name: id, required: true }, inputProps)) })), _jsxs("label", __assign({ htmlFor: id, className: "flex justify-between self-center items-center gap-1 text-sm-reg-m md:text-sm-reg-d" }, { children: [label, " ", _jsx(Asterisk, {})] })), suggestions.length > 0 && (_jsxs("div", __assign({ className: "absolute w-full md:max-w-sm border-2 border-shoe rounded-[10px] shadow mt-7" }, { children: [suggestions.map(function (suggestion) {
                                var className = clsx('first-of-type:rounded-t-[10px]', suggestion.active
                                    ? 'border-t border-b bg-squash-100 cursor-pointer border-squash-700 px-4 py-2 hover:bg-squash-100 focus:bg-squash-100'
                                    : 'border-b bg-bone cursor-pointer border-bowl-200 px-4 py-2 hover:bg-squash-100 focus:bg-squash-100 hover:border-squash-700');
                                return (_createElement("div", __assign({}, getSuggestionItemProps(suggestion, { className: className }), { key: suggestion.placeId }),
                                    _jsx("div", __assign({ className: "text-sm" }, { children: suggestion.formattedSuggestion.mainText })),
                                    _jsx("div", __assign({ className: "text-sm" }, { children: suggestion.formattedSuggestion.secondaryText }))));
                            }), _jsx("div", __assign({ className: "bg-bone px-4 py-2 rounded-b-[10px]" }, { children: _jsx("img", { src: powerByGoogleSrc }) }))] }))), error && (_jsx("div", __assign({ className: clsx('border-error-red bg-surface-white', 'w-full border-2 rounded-sm2 p-2xs-gutter text-center') }, { children: _jsx("span", __assign({ id: "".concat(id, "Error"), className: "text-sm-reg-m md:text-sm-reg-d text-center self-center" }, { children: error })) })))] })));
        } })));
};
// globalName is the name of the global that the script attaches to `window`.
// Async-script will pass this as a prop to the wrapped component
export default scriptLoader(_PLACES_API_URL, { globalName: 'google' })(LocationSearchInput);
